import type { MaybeRefOrGetter } from 'vue';
import { formatTitle } from '@directus/format-title';
import { computed, shallowRef, toValue } from 'vue';

export type FormattedDisplayPrimitive = string | number;

type FormattedDisplayValue = FormattedDisplayPrimitive | readonly FormattedDisplayPrimitive[] | null | undefined;

interface FormattedDisplayValueCache {
	locale: string;
	convertToTitleCase: boolean;
	// Hold the translate function identity so locale changes that only swap
	// the translator (without changing the locale string) still invalidate.
	translateString: (value: string) => string;
	valueSnapshot: FormattedDisplayValue;
	result: string;
}

export interface UseFormattedDisplayValueOptions {
	value: MaybeRefOrGetter<FormattedDisplayValue>;
	convertToTitleCase: MaybeRefOrGetter<boolean | string | undefined>;
	locale: MaybeRefOrGetter<string>;
	translateString: (value: string) => string;
}

/**
 * Formats primitive display values while caching the last rendered output for
 * recycled table rows that revisit the same value.
 */
export function useFormattedDisplayValue(options: UseFormattedDisplayValueOptions) {
	const cache = shallowRef<FormattedDisplayValueCache>();

	return computed(() => {
		const value = toValue(options.value);
		const locale = toValue(options.locale);
		const convertToTitleCase = Boolean(toValue(options.convertToTitleCase));
		const cached = cache.value;

		if (cached
			&& cached.locale === locale
			&& cached.convertToTitleCase === convertToTitleCase
			&& cached.translateString === options.translateString
			&& isSameFormattedDisplayValue(cached.valueSnapshot, value)) {
			return cached.result;
		}

		const valueSnapshot = cloneFormattedDisplayValue(value);
		const result = formatDisplayValue(valueSnapshot, options.translateString, convertToTitleCase);

		cache.value = {
			locale,
			convertToTitleCase,
			translateString: options.translateString,
			valueSnapshot,
			result,
		};

		return result;
	});
}

/**
 * Creates a stable snapshot so in-place array mutation still invalidates the cache.
 */
function cloneFormattedDisplayValue(value: FormattedDisplayValue): FormattedDisplayValue {
	if (Array.isArray(value)) {
		return [...value];
	}

	return value;
}

/**
 * Compares primitive scalar values or primitive arrays without requiring the same array identity.
 */
function isSameFormattedDisplayValue(previous: FormattedDisplayValue, next: FormattedDisplayValue): boolean {
	if (Array.isArray(previous) || Array.isArray(next)) {
		if (!Array.isArray(previous) || !Array.isArray(next) || previous.length !== next.length) {
			return false;
		}

		for (const [index, element] of previous.entries()) {
			if (!Object.is(element, next[index])) {
				return false;
			}
		}

		return true;
	}

	return Object.is(previous, next);
}

/**
 * Formats either a scalar primitive or an array while avoiding temporary arrays.
 */
function formatDisplayValue(
	value: FormattedDisplayValue,
	translateString: UseFormattedDisplayValueOptions['translateString'],
	convertToTitleCase: boolean,
): string {
	if (value == null) {
		return '';
	}

	// `Array.isArray` does not narrow readonly arrays, so cast after the negative check.
	if (!Array.isArray(value)) {
		return formatDisplayPrimitive(value as FormattedDisplayPrimitive, translateString, convertToTitleCase);
	}

	let result = '';

	for (const [index, element] of value.entries()) {
		const formatted = formatDisplayPrimitive(element, translateString, convertToTitleCase);
		result += index === 0 ? formatted : `, ${formatted}`;
	}

	return result;
}

/**
 * Formats one primitive value using the existing translation and title-case rules.
 */
function formatDisplayPrimitive(
	value: FormattedDisplayPrimitive,
	translateString: UseFormattedDisplayValueOptions['translateString'],
	convertToTitleCase: boolean,
): string {
	const translated = translateString(String(value));

	if (convertToTitleCase) {
		return formatTitle(translated);
	}

	return translated;
}
